import React, { useState, useContext, useRef } from 'react';
import { QrReader } from 'react-qr-reader';
import axiosConn from '../../axiosConn';
import { AuthContext } from '../../context/AuthContext';
import { useConfettiText } from '../../hooks/useConfettiText';
import useMobileView from '../../hooks/useMobileView';

const QrScanUnlock = ({ clueId, disabled = false, unlockedClues = [] }) => {
  const [result, setResult] = useState('');
  const { player } = useContext(AuthContext);
  const mobileView = useMobileView();
  const qrUnlockRef = useRef(null);
  const [facingMode, setFacingMode] = useState('environment');

  const [showConfettiText, ConfettiTextComponent] = useConfettiText(
    qrUnlockRef,
    mobileView,
    '+5 JGK QR Unlocked!',
    1000,
    'qrUnlock',
  );

  const playUnlockSound = () => {
    const audio = new Audio(
      'https://res.cloudinary.com/joegalaxy/video/upload/v1752624122/Futuristic_HUD_Sound_Design_Blake_Sanchez_touovl.mp3',
    );
    audio.currentTime = 0;
    audio.play();
    setTimeout(() => {
      audio.pause();
      audio.currentTime = 0;
    }, 1000);
  };

  const handleScan = async (data) => {
    if (data) {
      try {
        const res = await axiosConn.post(
          `/clues/unlock/qr/${clueId}`,
          { code: data },
          {
            headers: {
              Authorization: `Bearer ${player?.token}`,
            },
          },
        );
        setResult(res.data.message);
        showConfettiText(); // trigger confetti
        playUnlockSound(); // play sound
      } catch (err) {
        setResult(err.response?.data?.message || 'Error verifying QR code');
      }
    }
  };

  const handleError = (err) => {
    // Only show error if it's a real camera error, not a decode error
    if (
      err &&
      err.name &&
      (err.name === 'NotAllowedError' ||
        err.name === 'NotFoundError' ||
        err.name === 'NotReadableError' ||
        err.name === 'OverconstrainedError')
    ) {
      setResult('Error accessing camera');
    } else {
      // Ignore decode errors
      setResult('');
    }
  };

  if (disabled) {
    return (
      <div
        ref={qrUnlockRef}
        className="text-center"
        style={{
          fontSize: 28,
          margin: '18px 0',
          animation: 'pop 0.7s',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: 8,
          color: '#ef4444',
        }}
      >
        <span role="img" aria-label="qr" style={{ fontSize: 38 }}>
          🎉
        </span>
        <span style={{ fontSize: 22, fontWeight: 700 }}>
          This QR clue is already unlocked!
        </span>
        <ConfettiTextComponent />
        <span
          className="badge"
          style={{
            background: '#e5e7eb',
            color: '#ef4444',
            fontWeight: 700,
            fontSize: 15,
            borderRadius: 10,
            marginTop: 8,
            boxShadow: '0 2px 8px #ef444422',
            letterSpacing: 0.5,
            animation: 'pop 0.7s',
          }}
        >
          <span role="img" aria-label="check">
            ✔
          </span>{' '}
          Solved!
        </span>
        <style>
          {`
            @keyframes pop {
              0% { transform: scale(0.7); opacity: 0; }
              60% { transform: scale(1.2); opacity: 1; }
              100% { transform: scale(1); }
            }
          `}
        </style>
      </div>
    );
  }

  return (
    <div className="text-center">
      <button
        className="btn btn-sm btn-secondary mb-1"
        onClick={
          () => {
            setFacingMode(
              facingMode === 'environment' ? 'user' : 'environment',
            );
            setResult('');
          } // Clear error/result when switching camera
        }
      >
        Switch Camera
      </button>
      <QrReader
        constraints={{ facingMode }}
        onResult={(result, error) => {
          if (!!result) {
            setResult('');
            handleScan(result?.text);
          }
          if (!!error) {
            handleError(error);
          }
        }}
        style={{ width: '100%' }}
      />
      {result && <p className="mt-2 small text-muted">{result}</p>}
    </div>
  );
};
export default QrScanUnlock;
